<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import FullPageModal from '@/js/core/components/parts/FullPageModal.vue';

const props = defineProps({
  media: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const TYPE_NOUNS = { image: 'image', audio: 'audio', video: 'video', pdf: 'PDF' };

const mediaType = computed(() => props.media?.media_type || '');
const description = computed(() => String(props.media?.alt || '').trim());
const caption = computed(() => String(props.media?.caption || '').trim());
const url = computed(() => props.media?.url || '');

const dialogLabel = computed(() => {
  const noun = TYPE_NOUNS[mediaType.value] || 'media';
  return description.value ? `${noun}: ${description.value}` : `${noun} viewer`;
});

const close = () => {
  emit('close');
};

// Escape must close only this nested viewer, never the tray modal beneath it.
// Both FullPageModal instances listen for Escape in the bubble phase, so this
// capture-phase listener runs first and stops the event from reaching either.
// Once pointer focus enters the embedded PDF browsing context the key may never
// reach this document, which is why the visible Close control is always present.
const handleKeydown = event => {
  if (event.key !== 'Escape') return;

  event.stopPropagation();
  event.preventDefault();
  close();
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown, true);
});
</script>

<template>
  <FullPageModal
    role="dialog"
    :aria-label="dialogLabel"
    wrapper-class="is-comment-media-modal"
    move-focus-on-mount
    @close="close"
  >
    <div class="comment-media-gallery">
      <div class="comment-media-gallery__stage">
        <img
          v-if="mediaType === 'image'"
          :alt="description"
          :src="url"
          class="comment-media-gallery__image"
          @contextmenu.prevent
        />
        <audio
          v-else-if="mediaType === 'audio'"
          :src="url"
          :aria-label="dialogLabel"
          class="comment-media-gallery__player"
          controls
          controlslist="nodownload"
          @contextmenu.prevent
        ></audio>
        <video
          v-else-if="mediaType === 'video'"
          :src="url"
          :aria-label="dialogLabel"
          class="comment-media-gallery__player comment-media-gallery__player--video"
          controls
          controlslist="nodownload"
          @contextmenu.prevent
        ></video>
        <object
          v-else-if="mediaType === 'pdf'"
          :data="url"
          type="application/pdf"
          tabindex="-1"
          :aria-label="dialogLabel"
          class="comment-media-gallery__pdf"
        >
          <p class="comment-media-gallery__fallback-text">
            This PDF can't be displayed in this browser.
          </p>
        </object>
      </div>

      <p v-if="caption" class="comment-media-gallery__caption">{{ caption }}</p>
    </div>
  </FullPageModal>
</template>
